import React, { useState, useMemo, useEffect } from 'react';
import { Text } from '@cuemath/web-comps';
import { Separator } from '@cuemath/comps';
import {
  useGetAgents,
  useGetSupportTeams,
  usePatchTicket,
  invalidateTickets,
} from 'actions/support-agent';
import {
  ERROR_AUTO_CLEAR_MS,
  TRANSFER_TAB,
  TRANSFER_ACTION,
  OWNER_TYPE,
  TRANSFER_LABELS,
  TRANSFER_ERRORS,
} from '../constants';
import {
  ModalOverlay,
  ModalCard,
  ModalHead,
  ModalBody,
  ModalFoot,
  BtnSecondary,
  BtnPrimary,
  IconBtn,
  TransferSection,
  TransferLabel,
  TransferNote,
  ModalErrorMsg,
  TabBar,
  TabButton,
} from '../styled-comps';
import Icon from './icon';
import { AgentListSection, TeamListSection } from './transfer-modal-sections';

// ── Component ──────────────────────────────────────────────────────────────
const TransferModal = ({ open, onClose, ticketId, currentTeamId, onTicketUpdated }) => {
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [activeTab, setActiveTab] = useState(TRANSFER_TAB.TEAMS.key);
  const [transferNote, setTransferNote] = useState('');
  const [transferError, setTransferError] = useState(null);

  // --- API hooks ---
  const {
    data: agentsData,
    get: getAgents,
    isProcessingFailed: agentsFailed,
  } = useGetAgents();
  const { patch: patchTicket, isProcessing: transferProcessing } = usePatchTicket({
    onComplete: error => {
      if (!error) {
        setTransferError(null);
        invalidateTickets();
        if (onTicketUpdated) onTicketUpdated();
        onClose();
      } else {
        setTransferError(
          typeof error === 'string' ? error : TRANSFER_ERRORS.TRANSFER_FAILED,
        );
      }
    },
  });

  const {
    data: teamsData,
    get: getTeams,
    isProcessingFailed: teamsFailed,
  } = useGetSupportTeams();

  const agents = useMemo(() => agentsData?.agents || [], [agentsData]);
  const teams = useMemo(() => teamsData?.teams || [], [teamsData]);

  const onlineAgents = useMemo(() => agents.filter(a => a.is_online || a.online), [
    agents,
  ]);
  const offlineAgents = useMemo(() => agents.filter(a => !a.is_online && !a.online), [
    agents,
  ]);
  const sortedTeams = useMemo(
    () => teams.slice().sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [teams],
  );

  // Fetch data when modal opens
  useEffect(() => {
    if (open) {
      getAgents('agents');
      getTeams('member_counts');
      setSelectedAgent(null);
      setSelectedTeam(null);
      setTransferNote('');
      setTransferError(null);
      setActiveTab(TRANSFER_TAB.TEAMS.key);
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-clear transfer error
  useEffect(() => {
    if (!transferError) return;
    const timer = setTimeout(() => setTransferError(null), ERROR_AUTO_CLEAR_MS);
    return () => clearTimeout(timer);
  }, [transferError]);

  const handleTransfer = () => {
    if (!ticketId) return;
    if (activeTab === TRANSFER_TAB.AGENTS.key && selectedAgent) {
      patchTicket(ticketId, {
        action: TRANSFER_ACTION,
        owner_id: selectedAgent.id,
        owner_type: OWNER_TYPE.AGENT,
        ...(transferNote.trim() && { note: transferNote.trim() }),
      });
      return;
    }
    if (activeTab === TRANSFER_TAB.TEAMS.key && selectedTeam) {
      patchTicket(ticketId, {
        action: TRANSFER_ACTION,
        owner_id: selectedTeam.id,
        owner_type: OWNER_TYPE.TEAM,
      });
    }
  };

  const handleSelectTeam = team => {
    setSelectedTeam(team);
    setSelectedAgent(null);
  };

  if (!open) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalCard onClick={e => e.stopPropagation()}>
        <ModalHead>
          <Text variant="body1Medium">{TRANSFER_LABELS.TITLE}</Text>
          <IconBtn onClick={onClose}>
            <Icon name="close" size={16} />
          </IconBtn>
        </ModalHead>

        <ModalBody>
          {agentsFailed && <ModalErrorMsg>{TRANSFER_ERRORS.LOAD_AGENTS}</ModalErrorMsg>}
          {transferError && <ModalErrorMsg>{transferError}</ModalErrorMsg>}

          <TabBar>
            <TabButton
              $active={activeTab === TRANSFER_TAB.TEAMS.key}
              onClick={() => setActiveTab(TRANSFER_TAB.TEAMS.key)}
            >
              {TRANSFER_TAB.TEAMS.label}
            </TabButton>
            <TabButton
              $active={activeTab === TRANSFER_TAB.AGENTS.key}
              onClick={() => setActiveTab(TRANSFER_TAB.AGENTS.key)}
            >
              {TRANSFER_TAB.AGENTS.label}
            </TabButton>
          </TabBar>

          {activeTab === TRANSFER_TAB.AGENTS.key && (
            <>
              <AgentListSection
                label={`${
                  TRANSFER_LABELS.ONLINE
                } ${TRANSFER_TAB.AGENTS.label.toLowerCase()}`}
                agents={onlineAgents}
                isOnline
                selectedId={selectedAgent?.id}
                onSelect={setSelectedAgent}
              />

              {offlineAgents.length > 0 && (
                <>
                  <Separator height={4} transparent />
                  <AgentListSection
                    label={`${
                      TRANSFER_LABELS.OFFLINE
                    } ${TRANSFER_TAB.AGENTS.label.toLowerCase()}`}
                    agents={offlineAgents}
                    isOnline={false}
                    selectedId={selectedAgent?.id}
                    onSelect={setSelectedAgent}
                  />
                </>
              )}

              <Separator height={4} transparent />

              <TransferSection>
                <TransferLabel>{TRANSFER_LABELS.TRANSFER_NOTE}</TransferLabel>
                <TransferNote
                  placeholder={TRANSFER_LABELS.TRANSFER_NOTE_PLACEHOLDER}
                  value={transferNote}
                  onChange={e => setTransferNote(e.target.value)}
                />
              </TransferSection>
            </>
          )}

          {activeTab === TRANSFER_TAB.TEAMS.key && (
            <>
              {teamsFailed && <ModalErrorMsg>{TRANSFER_ERRORS.LOAD_TEAMS}</ModalErrorMsg>}
              <TeamListSection
                teams={sortedTeams}
                currentTeamId={currentTeamId}
                selectedId={selectedTeam?.id}
                onSelect={handleSelectTeam}
              />
            </>
          )}
        </ModalBody>

        <ModalFoot>
          <BtnSecondary onClick={onClose}>{TRANSFER_LABELS.CANCEL}</BtnSecondary>
          <BtnPrimary
            disabled={
              transferProcessing ||
              (activeTab === TRANSFER_TAB.AGENTS.key && !selectedAgent) ||
              (activeTab === TRANSFER_TAB.TEAMS.key && !selectedTeam)
            }
            onClick={handleTransfer}
          >
            {transferProcessing ? TRANSFER_LABELS.TRANSFERRING : TRANSFER_LABELS.TRANSFER}
          </BtnPrimary>
        </ModalFoot>
      </ModalCard>
    </ModalOverlay>
  );
};

export default TransferModal;
